import SvgImage from '@/components/Module/SvgImage';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { StyledEmpty } from '@/components/Approval/Content/ApprovalFIlters/styles';
import React, { useCallback, useRef, useState } from 'react';
import { INIT_WEB_DOCUMENT_FILTER_STATE } from '@/recoil/init_data';
import TalkFLowModal from '@/components/Record/TalkFLowModal';
import { t } from 'i18next';
import DocumentFilterButton from '@/components/Approval/Content/DocumentFilters/DocumentFilterButton';
import {
  FavoriteChecked,
  FavoriteDefault,
} from '@/theme/overrides/CustomIcons';
import styled from '@emotion/styled';
import FeedBackLink from '@/components/Approval/LeftContainer/FilterList/FeedBackLink';
import useDocumentFilters from '@/components/Approval/Content/hooks/useDocumentFilters';
import { Box, MenuItem, Tooltip } from '@mui/material';
import CategoryContent from '@/components/Approval/CategoryContent';
import ContentBannerCarousel from '@/components/ContentBannerCarousel';
import BoardDetailModal, {
  BoardDetailModalRef,
} from '@/components/Board/BoardDetailModal';
import { WorkEventName } from '@/constant/gtm';
import { useRecorder } from '@/components/RecorderProvider';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { useContentBanners, useSetAudioFile } from '@/hooks/apiHooks';
import { captureRecordError } from '@/components/Record/util/sentry';
import { isAttachFailedDialogId } from '@/components/Record/util/attachFailedDialog';
import { isUploadFailedDialogId } from '@/utils/audioUploadQueue';
import { Recording, RecordDataState } from '@/types/api';
import { useAtomValue } from 'jotai';
import { uploadingState, UploadingItem } from '@/recoil/atom';
import { apiURL } from '@/constant/apiURL';
import { useSWRConfig } from 'swr';
import { BoardDetailMenu } from '@/components/Board/BoardDetail/styles';
import RecordIcon from '@/public/icons/record/record.svg';

/**
 * 녹음 파일을 올리는 중이거나, 다 올린 녹음을 문서 화면이 문서에 연결하는 중인지.
 * 연결 중에는 isUploading 이 이미 false 라, 문서 화면이 표시하는 연결 진행 항목(uploadingState.record)으로 함께 본다.
 */
const isSavingRecording = (
  state: RecordDataState,
  uploadingRecords: UploadingItem[],
) =>
  state.isUploading ||
  (!!state.recordingData &&
    uploadingRecords.some((item) => item.name === state.recordingData?.name));

/** 이 화면의 다른 GA 이벤트(문서 필터)와 같은 area_name 을 쓴다. 배너 구분은 description 으로. */
const BANNER_AREA_NAME = 'Document 목록';

export const FilterList = [
  { value: 'bookMark', title: '즐겨찾기', id: 'bookMark' },
  { value: 'favoriteFilter', title: '내 필터', id: 'favoriteFilter' },
  { value: 'all', title: '전체 문서', id: 'all' },
  { value: 'write', title: '내가 작성자인', id: 'write' },
  { value: 'referrer', title: '공유받은 문서', id: 'referrer' },
];

const DocumentFilters = () => {
  const recorder = useRecorder();
  const { isRecordingInProgress, state: recorderState } = recorder;
  const uploadingRecords = useAtomValue(uploadingState).record;
  const { showError, showSuccess } = useSnackbar();
  const dialog = useDialog();
  const { mutate } = useSWRConfig();
  // 문서 생성을 기다리는 사이 녹음 상태가 바뀔 수 있어, 녹음을 시작할 때 최신 상태로 다시 본다
  const recorderRef = useRef(recorder);
  recorderRef.current = recorder;
  const uploadingRecordsRef = useRef(uploadingRecords);
  uploadingRecordsRef.current = uploadingRecords;
  const dialogStackRef = useRef(dialog.stack);
  dialogStackRef.current = dialog.stack;

  /**
   * 창을 닫아 아직 문서에 붙지 못한 이전 녹음을 원래 문서에 저장한다.
   * 새 녹음을 막지 않도록 새 녹음이 시작된 뒤 뒤에서 진행하고, 시작 직전에 잡아 둔 값만 쓴다.
   * 문서 화면을 거치지 않아 AI 자동 정리는 돌지 않는다 — 그 문서의 '재정리 요청'으로 대신한다 (근본 해결은 PORTAL-15095)
   */
  const savePreviousRecording = useCallback(
    async (
      docId: string,
      { name, uuid, duration }: Omit<Recording, 'state'>,
    ) => {
      try {
        await useSetAudioFile(docId, { name, uuid, duration });
        // 원래 문서의 상세 캐시는 녹음이 없던 때 그대로라, 다시 열면 녹음이 안 보인다 — 비우고 다시 받게 한다
        mutate(apiURL.TALK_FLOW_DOCUMENT_BY_ID(docId), undefined, {
          revalidate: true,
        });
        showSuccess({ message: t('이전 녹음을 원래 문서에 저장했어요.') });
        return;
      } catch (e) {
        captureRecordError('attach', e, { docId, title: name, uuid, duration });
      }

      // 파일 이름이 녹음 시작 시각(YYYY-MM-DD HH:mm:ss)이라, 분까지만 잘라 어떤 녹음인지 보여 준다
      const recordedAt =
        name.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/)?.[0] ??
        name.replace(/\.[^.]+$/, '');
      dialog.show({
        id: `record-previous-save-failed-${uuid}`,
        message: t(
          '이전 녹음을 원래 문서에 저장하지 못했어요.\n{{recordedAt}} 녹음 · {{duration}}',
          { recordedAt, duration },
        ),
        cancelButtonText: t('버리기'),
        confirmButtonText: t('다시 저장'),
        onConfirm: () => savePreviousRecording(docId, { name, uuid, duration }),
        // 버튼으로만 결정하게 한다
        isBlockBackDropClose: true,
        isBlockEscapeClose: true,
        showCloseButton: false,
      });
    },
    [dialog, mutate, showSuccess],
  );

  // 모달을 연 직후 기다림 없이 부른다 — 다른 녹음이 진행 중이면 덮어쓰지 않고 문서만 열어 둔다
  const startRecording = useCallback(
    async (docId: string) => {
      const { isRecordingInProgress, state, start } = recorderRef.current;
      if (
        isRecordingInProgress ||
        isSavingRecording(state, uploadingRecordsRef.current)
      ) {
        showError({ message: t('다른 녹음이 진행 중이라 문서만 만들었어요.') });
        return false;
      }
      // 녹음 업로드·연결 실패 안내가 하나라도 떠 있으면 시작하지 않는다 —
      // 그 안내의 '다시 시도'·'취소'가 녹음 상태를 새로 써서 새 녹음의 저장 대상까지 바꾸거나 지운다.
      // 같은 녹음을 여러 곳에서 연결하다 일부만 실패하면 남은 녹음 정보와 안내가 어긋날 수 있어, 녹음을 가리지 않는다
      const hasFailedDialog = dialogStackRef.current.some(
        ({ id }) => isUploadFailedDialogId(id) || isAttachFailedDialogId(id),
      );
      if (hasFailedDialog) {
        showError({
          message: t('이전 녹음을 저장하지 못해 문서만 만들었어요.'),
        });
        return false;
      }
      // 새 녹음이 녹음 상태를 새로 쓰기 전에, 붙지 못한 이전 녹음과 그 문서를 잡아 둔다
      const previous = state.recordingData;
      const previousDocId = state.docId;
      const started = await start(docId);
      if (started && previous) savePreviousRecording(previousDocId, previous);
      return started;
    },
    [savePreviousRecording, showError],
  );
  const {
    pageArea,
    documentState,
    setDocumentState,
    selectedId,
    favoriteData,
    documentCount,
    bookMarkCount,
    recordModal,
    handleFilterClick,
    handleFavoriteClick,
    handleNewDocumentClick,
    handleNewDocumentWithRecordingClick,
    handleNewMeetingWithRecordingClick,
    sendGTMEvent,
    onAfterClose,
  } = useDocumentFilters({ startRecording });
  // 녹음 중·업로드 중에 새로 시작하면 진행 중인 녹음 상태를 덮어쓰므로 녹음 시작을 막는다 (메뉴의 새 문서 작성은 그대로)
  const recorderBusyMessage = isRecordingInProgress
    ? t('다른 문서에서 녹음 중이에요.')
    : isSavingRecording(recorderState, uploadingRecords)
    ? t('녹음 파일 업로드 중입니다.')
    : '';
  const isRecorderBusy = Boolean(recorderBusyMessage);
  const [recordMenuAnchor, setRecordMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const recordButtonRef = useRef<HTMLDivElement>(null);
  const boardDetailModalRef = useRef<BoardDetailModalRef>(null);
  const { data: banners = [] } = useContentBanners('meeting_minutes');

  return (
    <div>
      {/* '녹음 시작'은 바로 녹음, ▾ 는 다른 작성 방식 메뉴 */}
      <Box ref={recordButtonRef} sx={{ display: 'flex', marginTop: '32px' }}>
        <Tooltip title={recorderBusyMessage} arrow placement={'top'}>
          {/* 비활성 버튼은 마우스 이벤트가 없어 툴팁이 뜨도록 감싼다 */}
          <span style={{ display: 'flex', flex: 1 }}>
            <BoxButton
              variant="primary"
              size="extraLarge"
              startIcon={<RecordIcon />}
              onClick={handleNewDocumentWithRecordingClick}
              disabled={isRecorderBusy}
              style={{
                flex: 1,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              {t('DocumentFilter.녹음 시작')}
            </BoxButton>
          </span>
        </Tooltip>
        <BoxButton
          variant="primary"
          size="extraLarge"
          aria-label={t('DocumentFilter.작성 방식 선택')}
          aria-haspopup="menu"
          aria-expanded={Boolean(recordMenuAnchor)}
          onClick={() => setRecordMenuAnchor(recordButtonRef.current)}
          startIcon={
            <SvgImage
              name={'arrow_down'}
              width={24}
              height={24}
              color={'currentColor'}
            />
          }
          style={{
            minWidth: 56,
            padding: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeftColor: 'var(--dk-inverse-bg-hover, #4D5157)',
          }}
        />
      </Box>
      <BoardDetailMenu
        anchorEl={recordMenuAnchor}
        open={Boolean(recordMenuAnchor)}
        onClose={() => setRecordMenuAnchor(null)}
        sx={{
          // 버튼과 양 끝을 맞춰 하나로 이어 보이게 한다
          '& .MuiPaper-root': {
            marginTop: '4px',
            width: recordMenuAnchor?.offsetWidth,
          },
          '& .MuiMenu-list': {
            width: 'auto',
            '> li': { padding: '8px', borderRadius: '6px' },
          },
        }}
      >
        {[
          {
            title: '녹음 시작',
            description: '새 문서를 만들고 바로 녹음해요',
            onClick: handleNewDocumentWithRecordingClick,
            disabled: isRecorderBusy,
          },
          {
            title: '회의록으로 녹음 시작',
            description: '회의록 양식에 나를 참석자로 넣고 녹음해요',
            onClick: handleNewMeetingWithRecordingClick,
            disabled: isRecorderBusy,
          },
          {
            title: '새 문서 작성',
            description: '녹음 없이 문서만 만들어요',
            onClick: handleNewDocumentClick,
            disabled: false,
          },
        ].map(({ title, description, onClick, disabled }) => (
          <MenuItem
            key={title}
            disabled={disabled}
            onClick={() => {
              setRecordMenuAnchor(null);
              onClick();
            }}
          >
            <Box>
              <Box sx={{ fontSize: '14px', lineHeight: '20px' }}>
                {t(`DocumentFilter.${title}`)}
              </Box>
              <Box
                sx={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: 'var(--dk-text-secondary, #888D96)',
                }}
              >
                {t(`DocumentFilter.${description}`)}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </BoardDetailMenu>
      {/* 노출할 배너가 없으면 배너 칸째 숨긴다. */}
      {banners.length > 0 && (
        <Box
          sx={{
            background: 'var(--dk-bg, #ffffff)',
            borderTop: '1px solid var(--dk-line-strong, #D4D7DB)',
            borderBottom: '1px solid var(--dk-line-strong, #D4D7DB)',
            margin: '24px -24px 24px -30px',
          }}
        >
          <ContentBannerCarousel
            banners={banners}
            onBoardClick={(documentId) =>
              boardDetailModalRef.current?.open(documentId)
            }
            eventPageArea={pageArea}
            eventAreaName={BANNER_AREA_NAME}
            eventName={WorkEventName.clickEvent}
          />
        </Box>
      )}
      {/* 배너가 없을 때는 배너 추가 전 간격(12px)으로 버튼과 필터 사이를 띄운다. */}
      <Layout style={banners.length > 0 ? undefined : { paddingTop: 12 }}>
        {FilterList.map((item) => {
          const { value, id, title } = item;
          // 내 필터가 선택된 경우(id가 있는 경우)는 일반 필터 버튼은 active 안 됨
          const active = value === documentState.category && !documentState.id;

          // "내 필터" 항목은 CategoryContent를 사용
          if (value === 'favoriteFilter') {
            return (
              <CategoryContent
                key={value}
                id={id}
                title={t(`DocumentFilter.${title}`)}
                onClick={() => {
                  setDocumentState({
                    ...INIT_WEB_DOCUMENT_FILTER_STATE,
                    category: value,
                  });
                  sendGTMEvent(title);
                }}
                selectedId={selectedId}
                isDocumentFilter={true}
              >
                {favoriteData.length > 0 ? (
                  favoriteData.map((res, idx) => (
                    <CategoryContent
                      key={`favorite_filter-${idx}`}
                      id={res.id}
                      title={res.name}
                      onClick={(id) => {
                        handleFavoriteClick(id);
                        sendGTMEvent(res.name);
                      }}
                      selectedId={selectedId}
                      isChildren
                      favoriteId={res.id}
                      isDocumentFilter={true}
                    />
                  ))
                ) : (
                  <StyledEmpty>
                    {t(
                      '‘전체 문서’에서 자주 쓰는 필터를 설정해\n내 필터로 저장해 보세요.',
                    )}
                  </StyledEmpty>
                )}
              </CategoryContent>
            );
          }

          return (
            <DocumentFilterButton
              key={value}
              title={t(`DocumentFilter.${title}`)}
              onClick={() => handleFilterClick(item)}
              active={active}
              count={
                value === 'bookMark'
                  ? bookMarkCount || undefined
                  : value === 'referrer'
                  ? documentCount || undefined
                  : undefined
              }
              rightIcon={
                value === 'bookMark' ? (
                  active ? (
                    <FavoriteChecked />
                  ) : (
                    <FavoriteDefault />
                  )
                ) : undefined
              }
            />
          );
        })}
      </Layout>
      <TalkFLowModal ref={recordModal} onAfterClose={onAfterClose} />
      <BoardDetailModal
        ref={boardDetailModalRef}
        pageArea={pageArea}
        areaName={BANNER_AREA_NAME}
      />
      <FeedBackLink pageArea={pageArea} />
    </div>
  );
};
export default DocumentFilters;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
